/**
 * ContentComponent
 *
 * Content display component for list/feed mode. Combines VideoComponent with
 * user-generated content UI elements (user info, description, actions, links).
 *
 * No fullscreen layout management — the fullscreen toggle callbacks are simply
 * forwarded to the caller. For CSS-based fullscreen support see
 * ContentComponentFullScreenSupport.
 */

import React, { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectHas4kAccess } from '@/presentation/store/slices/premiumPackages/premiumPackagesSlice';
import { VideoComponent } from '@/presentation/components/video/VideoComponent';
import { ContentComponentOverlays } from '@/presentation/components/video/ContentComponentOverlays';
import { VideoProgressBar } from '@/presentation/components/video/VideoProgressBar';
import { UserInfoComponent } from './UserInfoComponent';
import { ExpandableRichText } from '@/presentation/components/common/text/ExpandableRichText';
import { ContentActions } from './ContentActions';
import { ContentLinksComponent } from './ContentLinksComponent';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { GiftEntity } from '@/domain/entities/gift/giftEntity';
import { MediaUtils } from '@/domain/utils/mediaUtils';
import { useIsLandscape, useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useUserMediaLike } from '@/presentation/feature/content/hooks/useUserMediaLike';
import { useVideoManagement } from '@/presentation/feature/content/hooks/useVideoManagement';
import { useUnlockContent } from '@/presentation/feature/content/hooks/useUnlockContent';
import { useContentPlaybackTracker } from '@/presentation/feature/content/hooks/useContentPlaybackTracker';
import { useExplicitContentGate } from '@/presentation/feature/content/hooks/useExplicitContentGate';
import { qaAttr, type QaAttrOptions } from '@/presentation/utils/qaAttr';
import { GiftUtils } from '@/domain/utils/giftUtils';

export interface ContentComponentProps {
  /** Screen key that will be used for video management and identification */
  screenKey: string;

  /** Media entity containing video information */
  media: UserMediaEntity;

  /** Whether to autoplay after initialization */
  autoplay?: boolean;

  /** Whether to mute video */
  muted?: boolean;

  /** Custom CSS class */
  className?: string;

  /** Current user ID */
  currentUserId?: string;

  /** Whether user is subscribed to content creator */
  subscribed?: boolean;

  /** Whether to show the more button in user info */
  showMore?: boolean;

  /** Video interaction callbacks */
  onClick?: (media: UserMediaEntity) => void;
  onInitStart?: (media: UserMediaEntity) => void;
  onReady?: (media: UserMediaEntity) => void;
  onPlaybackStart?: (media: UserMediaEntity) => void;
  onFullscreenClick?: (media: UserMediaEntity) => void;
  onFullscreenClose?: () => void;
  onVideoElementReady?: (media: UserMediaEntity, videoElement: HTMLVideoElement) => void;
  onVideoFullScreenStateChange?: (media: UserMediaEntity, state: {
    isPlaying: boolean;
    currentTime: number;
    duration: number;
    volume: number;
    isMuted: boolean;
  }) => void;
  onError?: (media: UserMediaEntity, error: Error) => void;

  /** Content UI interaction callbacks */
  onUserTap?: (userId: string) => Promise<void> | void;
  onMoreTap?: (userId: string) => Promise<void> | void;
  onHashtagTap?: (hashtag: string) => Promise<void> | void;
  onMentionTap?: (mention: string) => Promise<void> | void;
  onUrlTap?: (url: string) => Promise<void> | void;
  onLikeTap?: () => Promise<void> | void;
  onCommentTap?: () => Promise<void> | void;
  onGiftTap?: (defaultGift?: GiftEntity) => Promise<void> | void;
  onShareTap?: () => Promise<void> | void;
  onContentLinkTap?: () => Promise<void> | void;
  onCommentReplyTap?: () => Promise<void> | void;
  onScheduleTap?: () => Promise<void> | void;
  onDraftTap?: () => Promise<void> | void;
  onGet4kAccessTap?: () => Promise<void> | void;
  onNsfwTap?: (media: UserMediaEntity) => Promise<void> | void;
  onUnlockContentTap?: () => Promise<void> | void;
  onContentBlockedTap?: () => Promise<void> | void;

  /** QA tag for the row root (`data-qa-id`); a `data-qa-status` lock token is merged in. */
  qa?: QaAttrOptions;
}

export const ContentComponent: React.FC<ContentComponentProps> = ({
  screenKey,
  media,
  autoplay = false,
  muted = false,
  className = '',
  currentUserId,
  subscribed = false,
  showMore = false,
  onClick,
  onInitStart,
  onReady,
  onPlaybackStart,
  onFullscreenClick,
  onVideoElementReady,
  onVideoFullScreenStateChange,
  onError,
  onUserTap,
  onMoreTap,
  onHashtagTap,
  onMentionTap,
  onUrlTap,
  onLikeTap,
  onCommentTap,
  onGiftTap,
  onShareTap,
  onContentLinkTap,
  onCommentReplyTap,
  onScheduleTap,
  onDraftTap,
  onGet4kAccessTap,
  onNsfwTap,
  onUnlockContentTap,
  onContentBlockedTap,
  qa,
}) => {
  const isLandscape = useIsLandscape();
  const responsive = useResponsiveDesign();
  const has4kAccess = useSelector(selectHas4kAccess);

  const videoKey = MediaUtils.composeVideoPlayerKey(screenKey, media.id);
  const duration = media.duration ? media.duration / 1000 : 0;
  const typeLimit = media.typeLimit ? media.typeLimit / 1000 : null;
  const isOwner = !!currentUserId && currentUserId === media.firebaseId;
  const hasContentOrHashTags = (media.contentTags?.length ?? 0) > 0 || (media.hashTags?.length ?? 0) > 0;
  const { playbackStates, seekActiveVideo } = useVideoManagement();
  const playbackState = playbackStates[videoKey];

  const { likeData } = useUserMediaLike(media.relativePath);

  const { hasExclusiveRights, hasUnlockedContent } = useUnlockContent(media);

  const [isVideoReady, setIsVideoReady] = useState(false);

  const { typeLimitReached, defaultGift } = useContentPlaybackTracker({
    videoKey,
    typeLimitMs: media.typeLimit,
    hasExclusiveRights,
    isVideoReady,
    contentId: media.id,
    isContent: media.isContent,
    isOwner,
    hasContentOrHashTags,
    durationMs: media.duration ?? 0,
    ownerUserId: media.firebaseId,
    currentViews: media.views,
  });

  const { gated: nsfwGated, reveal: revealNsfw } = useExplicitContentGate({
    videoKey,
    media,
    isOwner,
    isNsfw: MediaUtils.isNsfwContent(media.explicit),
  });

  const handleSeek = useCallback((time: number) => {
    if (typeLimitReached && !hasExclusiveRights) return;
    seekActiveVideo(time);
  }, [typeLimitReached, hasExclusiveRights, seekActiveVideo]);

  const handleGiftTap = useCallback(() => {
    return onGiftTap?.(defaultGift);
  }, [onGiftTap, defaultGift]);

  // Lock token for the row root, derived from already-computed unlock state.
  // Only meaningful when the media is gated behind an unlock charge.
  const isUnlockable = !!media.allowUnlock && (media.unlockCharge ?? 0) > 0;
  const lockStatus = isUnlockable
    ? (hasExclusiveRights || hasUnlockedContent ? 'unlocked' : 'locked')
    : undefined;
  const rootQa = qa ? { ...qa, status: lockStatus } : undefined;

  // Playback token for the media surface, reflecting the live playback state.
  const playbackStatus = playbackState?.isPlaying ? 'playing' : 'paused';

  return (
    <div {...(rootQa ? qaAttr(rootQa) : {})} className={`flex flex-col w-full ${className}`}>
      <div style={{ paddingLeft: responsive.spacing.lg, paddingRight: responsive.spacing.lg }}>
        <UserInfoComponent
          userId={media.firebaseId!}
          contentId={media.id}
          contentCreatedTimestamp={media.createdTimestamp}
          views={media.views}
          showProfileImage={true}
          showDate={false}
          userName={media.userName || '--'}
          subscribed={subscribed}
          currentUserId={currentUserId}
          showMore={showMore}
          onUserTap={onUserTap || (async () => { })}
          onMoreTap={onMoreTap}
        />
      </div>

      {media.description && (
        <div style={{
          paddingLeft: responsive.spacing.lg,
          paddingRight: responsive.spacing.lg,
          paddingTop: responsive.spacing.lg,
        }}>
          <ExpandableRichText
            description={media.description}
            maxLines={3}
            onHashtagTap={onHashtagTap}
            onMentionTap={onMentionTap}
            onUrlTap={onUrlTap}
          />
        </div>
      )}

      <div style={{
        paddingLeft: responsive.spacing.lg,
        paddingRight: responsive.spacing.lg,
        paddingTop: responsive.spacing.base,
        paddingBottom: responsive.spacing.lg
      }}>
        <ContentLinksComponent
          media={media}
          showShare={currentUserId === media.firebaseId}
          isNsfw={MediaUtils.isNsfwContent(media.explicit)}
          isPremiumVideo={MediaUtils.isPremiumVideo(media.duration)}
          is4k={MediaUtils.is4kVideo(media.resolution)}
          currentUserId={currentUserId}
          has4kAccess={has4kAccess}
          hasUnlockedContent={hasUnlockedContent}
          hasExclusiveRights={hasExclusiveRights}
          onContentLinkTap={onContentLinkTap}
          onCommentReplyTap={onCommentReplyTap}
          onShareTap={onShareTap}
          onScheduleTap={onScheduleTap}
          onDraftTap={onDraftTap}
          onGet4kAccessTap={onGet4kAccessTap}
          onNsfwTap={onNsfwTap}
          onUnlockContentTap={onUnlockContentTap}
          onContentBlockedTap={onContentBlockedTap}
        />
      </div>

      <div
        {...qaAttr({ id: `content-playpause-${media.id}`, status: playbackStatus })}
        className={
        `relative
        ${isLandscape ? 'overflow-hidden' : ''}
        `
      }
        style={isLandscape ? { borderRadius: responsive.borderRadius.base } : undefined}>
        <VideoComponent
          screenKey={screenKey}
          media={media}
          autoplay={autoplay}
          muted={muted}
          isFullscreen={false}
          onClick={onClick}
          onInitStart={onInitStart}
          onReady={onReady}
          onPlaybackStart={(m) => { setIsVideoReady(true); onPlaybackStart?.(m); }}
          onFullscreenClick={onFullscreenClick}
          onVideoElementReady={onVideoElementReady}
          onVideoFullScreenStateChange={onVideoFullScreenStateChange}
          onError={onError}
        />

        {/* Full-size overlay: playback buttons.
            Sits above the preview thumbnail (z-30) so it's visible even
            before the video has been initialized. The overlay's backdrop is
            click-through, so taps still reach the thumbnail/video below. */}
        <div className="absolute inset-0 z-40 pointer-events-none">
          <ContentComponentOverlays
            screenKey={screenKey}
            media={media}
            playbackState={playbackState}
            duration={duration}
            isVideoReady={isVideoReady}
            autoHide={true}
            onFullscreenClick={onFullscreenClick ? () => onFullscreenClick(media) : undefined}
            hasExclusiveRights={hasExclusiveRights}
            typeLimitReached={typeLimitReached}
            onUnlockContentTap={onUnlockContentTap}
            nsfwGated={nsfwGated}
            onNsfwReveal={revealNsfw}
          />
        </div>

        {/* Progress bar */}
        <div className="absolute bottom-0 z-40"
          style={{ left: responsive.spacing.lg, right: responsive.spacing.lg }}>
          <VideoProgressBar
            videoKey={videoKey}
            duration={duration}
            typeLimit={typeLimit}
            onSeek={handleSeek}
            isVideoReady={isVideoReady}
          />
        </div>
      </div>

      <div style={{ paddingLeft: responsive.spacing.lg, paddingRight: responsive.spacing.lg, paddingTop: responsive.spacing.lg }}>
        <ContentActions
          contentId={media.id}
          userId={media.firebaseId || ''}
          subscribed={subscribed}
          likes={media.likes}
          comments={media.comments}
          coins={media.coins}
          displayAddComment={true}
          showCoins={media.showCoins}
          defaultGiftUrl={defaultGift ? GiftUtils.getGiftPath(defaultGift.image) : undefined}
          isLiked={likeData?.liked || false}
          onUserTap={onUserTap || (async () => { })}
          onAddCommentTap={onCommentTap || (async () => { })}
          onLikeTap={onLikeTap || (async () => { })}
          onSendGiftTap={handleGiftTap}
          onMoreTap={onMoreTap ? () => onMoreTap(media.firebaseId || '') : undefined}
          onShareSocialContentTap={onShareTap || (async () => { })}
        />
      </div>

    </div>
  );
};
